import { useAuth } from '@/hooks/useAuth'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema } from '@/utils/validation'
import { LoginCredentials } from '@/types/auth'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { useTelegram } from '@/hooks/useTelegram'
import { motion } from 'framer-motion'

const LoginPage = () => {
  const { loginMutation } = useAuth()
  const { isTelegram } = useTelegram()

  const telegramEnv = import.meta.env.VITE_TELEGRAM
  const showTelegramMode = isTelegram && telegramEnv === 'true'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = (data: LoginCredentials) => {
    loginMutation.mutate(data)
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 bg-[var(--bg-page)] transition-colors duration-300 relative overflow-hidden`}>
      {/* Animated background gradients */}
      <motion.div
        className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -100, 0],
          y: [0, -50, 0],
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={`
          max-w-md w-full p-8 rounded-3xl relative z-10
          ${showTelegramMode ? 'bg-transparent shadow-none' : 'ios-card bg-[var(--bg-card)] shadow-2xl border border-[var(--border-light)]'}
        `}
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-gradient-to-tr from-blue-500 via-purple-500 to-pink-500 rounded-2xl mx-auto flex items-center justify-center text-4xl shadow-2xl shadow-purple-500/40 mb-4 relative overflow-hidden"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            <span className="relative z-10">📊</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold text-[var(--text-primary)]"
          >
            Report<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Sys</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[var(--text-secondary)] mt-2"
          >
            Вход в систему отчетности
          </motion.p>
        </div>

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              placeholder="name@company.com"
              autoComplete="email"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              }
              {...register('email')}
              error={errors.email?.message}
            />

            <Input
              label="Пароль"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
              {...register('password')}
              error={errors.password?.message}
            />
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            variant="gradient"
            isLoading={loginMutation.isPending}
            className="shadow-2xl shadow-purple-500/30"
          >
            {!loginMutation.isPending && (
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            )}
            Войти
          </Button>
        </motion.form>

        {/* Decorative elements */}
        <motion.div
          className="mt-8 text-center text-xs text-[var(--text-tertiary)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <p>Защищено end-to-end шифрованием 🔒</p>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default LoginPage
